import { IEjariContractContext } from "./contexts/EjariContractContext";
import { Contract, ContractValueType, MoveOut } from "@eres/ejari-api/Entity/contract";
import { EjariPropertyType } from "@eres/ejari-api/Entity";
import { EjariCoreConfig, TenancyContactAction } from "./constants";
import { CompanyTenant, PersonTenant, Tenant } from "@eres/ejari-api/Entity/tenant";
import { Util } from "@eres/core";
import { normalizeContract } from "@eres/ejari-api/ContractUtils";
import { EjariContractDataState } from "./reducers/EjariContractData";
import moment from "moment";

export function getEjariPaymentAction() {
	return EjariCoreConfig.getEjariActiveUserId() === 0 ? 13 : 2;
}

export function buildContractForSubmission( ctx: IEjariContractContext ): Contract {
	let eprop = ctx.property!;
	let epid = eprop.PropertyTypeId === EjariPropertyType.UNIT ? eprop.UnitEjariPropertyId : eprop.VillaEjariPropertyId;
	let contract: Contract = {
		EjariPropertyId: [
			Number( epid ),
		],
		PrimaryOwnerParticipantId: EjariCoreConfig.getParticipantID() + "",
		ActiveUserType: EjariCoreConfig.getEjariUserType(),
		PaymentAction: getEjariPaymentAction(),
		ActiveUserID: EjariCoreConfig.getEjariActiveUserId() + "",
		ManagementCompanyId: EjariCoreConfig.isOwnerLoggedIn() ? "" : EjariCoreConfig.getParticipantID() + "",
		OwnersParticipantId: EjariCoreConfig.isOwnerLoggedIn() ? EjariCoreConfig.getParticipantID() + "" : "",
		...( !ctx.isCommercial && { TotalCoTenants: ctx.TotalCoTenants } ),
	};
	contract = {
		...contract,
		...getTenantData( ctx ),
		...getContractData( ctx ),
		...getPaymentPlanData( ctx ),
		...getTermsData( ctx ),
		...getHazardsMaterialData( ctx ),
	};
	if ( ctx.action === TenancyContactAction.NEW_CONTRACT ) {
		contract = {
			...contract,
			...getMoveOutData( ctx ),
		};
	}
	contract = normalizeContract( contract );
	return contract;
}

export function getTenantData( ctx: IEjariContractContext ): Partial<Contract> {
	let data: {
		Tenants: Tenant[];
	} = {
		Tenants: [],
	};
	const tenantData = {
		...ctx.OrgTenant,
		...ctx.tenant
	};
	let tenant: Tenant = {
		IsPrimary: true,
	};
	tenant = {
		...tenant,
		...Util.subSet( tenantData, "Email", "Pobox", "TenantId", "TenantNumber", "TenantParticipantId" ),
	};
	if ( !tenantData.IsCompany ) {
		tenant = {
			...tenant,
			IsCompany: false,
			...Util.subSet( tenantData, "TenantArabicName", "TenantEnglishName" ),
		};
		let person: PersonTenant = {
			...Util.subSet( tenantData.Person, "DOB", "EmiratesId", "Gender", "IsGCCNational", "Mobile", "NationalityId", "PassportExpiryDate",
				"PassportIssueDate", "PassportIssuePlace", "PassportIssuePlaceAr", "PassportNo", "PlaceOfBirthID", "ResidenceVisaNumber",
				"UIdNumber", "VatNumber", "VisaExpiryDate", "VisaStartDate" ),
		};
		if ( person.IsGCCNational && person.EmiratesId == null ) person.EmiratesId = "0";
		if ( ctx.isCommercial ) {
			person = {
				...person,
				...Util.subSet( tenantData.Person, "LicenseIssuerEmirate", "LicenseIssuer" ),
			};
			if ( tenantData.isGovernment === true ) {
				person = {
					...person,
					...Util.subSet( tenantData.Person, "GovernmentComanyNameAr", "GovernmentComanyNameEn" ),
				};
			} else {
				person = {
					...person,
					...Util.subSet( tenantData.Person, "LicenseNo" ),
				};
			}
			// revise
			if ( tenantData.Person?.LicenseStateId != null ) {
				person = {
					...person,
					...Util.subSet( tenantData.Person, "LicenseStateId" ),
				};
			}
		}
		tenant.Person = person;
	} else {
		tenant = {
			...tenant,
			IsCompany: true,
			...Util.subSet( tenantData, "TenantArabicName", "TenantEnglishName" ),
		};
		let company: CompanyTenant = {
			...Util.subSet( tenantData.Company, "LicenseIssuerEmirate", "LicenseExpiryDate", "LicenseIssueDate",
				"LicenseIssuer", "IsInitialApproval", "ContactPersonMobile", "ContactPersonName" ),
		};
		if ( tenantData.isGovernment === true ) {
			company = {
				...company,
				...Util.subSet( tenantData.Company, "GovernmentComanyNameAr", "GovernmentComanyNameEn" ),
			};
		} else {
			company = {
				...company,
				...Util.subSet( tenantData.Company, "LicenseNo" ),
			};
		}

		if ( tenantData.hasLicState === true ) {
			company = {
				...company,
				...Util.subSet( tenantData.Company, "LicenseStateId" ),
			};
		}
		tenant.Company = company;
	}
	data.Tenants.push( tenant );
	return data;
}

export function getContractData( ctx: IEjariContractContext ): Partial<Contract> {
	const cd = ctx.finance!;
	let data = {
		ContractAmount: cd.ContractValue,
		ContractEndDate: cd.EndDate,
		ContractStartDate: cd.StartDate,
		ContractValueType: cd.ContractValueType,
	};
	if ( cd.ContractValueType === ContractValueType.EntirePeriod ) {
		data = {
			...data,
			AssociatedContractAnnualCosts: [],
			...Util.subSet( cd, "DiscountTypeId", "DiscountValue" ),
		};
	} else {
		data = {
			...data,
			...Util.subSet( cd, "AssociatedContractAnnualCosts" ),
		};
	}
	if ( cd.IsGracePeriod === true ) {
		data = {
			...data,
			...Util.subSet( cd, "GraceStartDate", "GraceEndDate" ),
		};
	}
	return data;
}

export function getPaymentPlanData( ctx: IEjariContractContext ): Partial<Contract> {
	let data: Partial<Contract> = {
		...Util.subSet( ctx, "SecurityDeposit", "NoOfPayments" ),
	};
	if ( ctx.Payments ) {
		data.PaymentPlan = ctx.Payments;
	} else data.PaymentPlan = [];
	return data;
}

export function getTermsData( ctx: IEjariContractContext ): Partial<Contract> {
	let data: Partial<Contract> = {
		IsTenantApprovalReq: ctx.tenantApproval ? 1 : 0,
		...Util.subSet( ctx, "AdditionalTerms" ),
	};
	return data;
}

function getHazardsMaterialData( ctx: IEjariContractContext ): Partial<Contract> {
	return { HazardousMaterialClassRels: ctx.hazardIds };
}

function getMoveOutData( ctx: IEjariContractContext ): Partial<Contract> {
	const cd: MoveOut | undefined = ctx.moveOut!;
	let data: Partial<Contract> = {};
	if ( cd ) {
		data = {
			...Util.subSet( cd, "DewaMoveinDate", "DewaMoveoutDate", "DewaMoveoutcontractNumber" ),
		};
	}
	return data;
}

export function buildRenewContext( ctx: IEjariContractContext ): Partial<EjariContractDataState> {
	const x: Partial<EjariContractDataState> = {};
	const { property, contract, miniContract } = ctx;
	if ( contract ) {
		if ( contract.Tenants && contract.Tenants.length > 0 ) {
			x.OrgTenant = contract.Tenants[ 0 ];
		}
		let start = contract.ContractEndDate?.asDate();
		start = moment( start ).add( "1", "days" ).toDate();
		x.OrgFinance = {
			StartDate: start.asServerString(),
			EndDate: moment( start ).add( 1, "years" ).add( -1, "days" ).toDate().asServerString()
		};
		x.SecurityDeposit = contract.SecurityDeposit;
		x.AdditionalTerms = contract.AdditionalTerms;
		x.hazardIds = contract.HazardousMaterialClassRels;
	}
	return x;
}
